import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getGameModule } from '../graphql/queries';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Save, Edit } from 'lucide-react';
import './GameModule.css';

import { 
    fetchModulesByCreator, 
    fetchModulesByStatus, 
    createGameModule, 
    updateGameModule, 
    deleteGameModule 
  } from './GameModuleAPI';

const client = generateClient();

const GameModuleForm = ({ moduleId = null }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [module, setModule] = useState({
    name: '',
    description: '',
    scenes: '{}',
    recommendedPlayers: 1,
    minLevel: 1,
    maxLevel: 1,
    maxPartySize: 1,
    startingLevel: 1,
    customSettings: '{}',
    difficulty: 'MEDIUM',
    estimatedPlayTime: '',
    tags: [],
    version: '1.0.0',
    status: 'DRAFT'
  });

  useEffect(() => {
    if (moduleId) {
      fetchModule();
    }
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const moduleData = await client.graphql({
        query: getGameModule,
        variables: { id: moduleId }
      });
      setModule(moduleData.data.getGameModule);
    } catch (error) {
      console.error('Error fetching module:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModule(prev => ({ ...prev, [name]: value }));
  };

  const handleJsonInputChange = (e) => {
    const { name, value } = e.target;
    try {
      JSON.parse(value); // Validate JSON
      setModule(prev => ({ ...prev, [name]: value }));
    } catch (error) {
      console.error(`Invalid JSON in ${name}:`, error);
    }
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim());
    setModule(prev => ({ ...prev, tags }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const input = {
        ...module,
        creatorId: user.username,
        scenes: JSON.parse(module.scenes),
        customSettings: JSON.parse(module.customSettings)
      };
      if (moduleId) {
        await client.graphql({
          query: updateGameModule,
          variables: { input: { id: moduleId, ...input } }
        });
      } else {
        await client.graphql({
          query: createGameModule,
          variables: { input }
        });
      }
      // Redirect or show success message
    } catch (error) {
      console.error('Error saving module:', error);
    }
  };

  return (
    <div className="game-module-form">
      <h2>{moduleId ? 'Edit' : 'Create'} Game Module</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Module Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={module.name}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={module.description}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="scenes">Scenes (JSON)</label>
          <textarea
            id="scenes"
            name="scenes"
            value={module.scenes}
            onChange={handleJsonInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="recommendedPlayers">Recommended Players</label>
          <input
            type="number"
            id="recommendedPlayers"
            name="recommendedPlayers"
            value={module.recommendedPlayers}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="minLevel">Minimum Level</label>
          <input
            type="number"
            id="minLevel"
            name="minLevel"
            value={module.minLevel}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="maxLevel">Maximum Level</label>
          <input
            type="number"
            id="maxLevel"
            name="maxLevel"
            value={module.maxLevel}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="maxPartySize">Maximum Party Size</label>
          <input
            type="number"
            id="maxPartySize"
            name="maxPartySize"
            value={module.maxPartySize}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="startingLevel">Starting Level</label>
          <input
            type="number"
            id="startingLevel"
            name="startingLevel"
            value={module.startingLevel}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="customSettings">Custom Settings (JSON)</label>
          <textarea
            id="customSettings"
            name="customSettings"
            value={module.customSettings}
            onChange={handleJsonInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="difficulty">Difficulty</label>
          <select
            id="difficulty"
            name="difficulty"
            value={module.difficulty}
            onChange={handleInputChange}
            required
          >
            <option value="EASY">Easy</option>
            <option value="MEDIUM">Medium</option>
            <option value="HARD">Hard</option>
            <option value="EXTREME">Extreme</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="estimatedPlayTime">Estimated Play Time</label>
          <input
            type="text"
            id="estimatedPlayTime"
            name="estimatedPlayTime"
            value={module.estimatedPlayTime}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="tags">Tags (comma-separated)</label>
          <input
            type="text"
            id="tags"
            name="tags"
            value={module.tags.join(', ')}
            onChange={handleTagsChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="version">Version</label>
          <input
            type="text"
            id="version"
            name="version"
            value={module.version}
            onChange={handleInputChange}
            required
          />
        </div>
        <button type="submit" className="submit-button">
          <Save size={16} />
          {moduleId ? 'Update' : 'Create'} Module
        </button>
      </form>
    </div>
  );
};

export default GameModuleForm;